import { useState, useEffect, useCallback } from "react";
import { ChevronRight, Completed } from "@/icons";
import { useSearchParams } from "react-router-dom";

import { Collapse } from "@/components/Layout/Collapse";
import { CollectionResponse, FullItems } from "@/api/content";
import { useTranslate } from "@/providers/context/useTranslate";
import { cn } from "@/utils";

import { ItemList } from "./components/ItemList";
import { Module, useCourse } from "./hooks/useCourse";
import { useCourseCompletion } from "./hooks/useCourseCompletion";

const getCategoryLessons = (category: Module): string[] => {
  let lessonIds = category.lessons.map((lesson) => lesson.ChildId);
  if (category.subcategories && category.subcategories.length > 0) {
    for (const sub of category.subcategories) {
      lessonIds = lessonIds.concat(sub.lessons.map((lesson) => lesson.ChildId));
    }
  }
  return lessonIds;
};

interface CourseOverviewProps {
  collection: CollectionResponse;
  isSidePanel?: boolean;
  className?: string;
}

const Heading = ({
  title,
  className,
}: {
  title: string;
  className?: string;
}) => {
  return <h2 className={cn("h4-extrabold", className)}>{title}</h2>;
};

interface CategoryProps {
  category: Module;
  fullItems: FullItems;
  courseId: number;
  isSidePanel?: boolean;
  isVideoWatched: (lessonId: string) => boolean;
  className?: string;
  isLast?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const Category = ({
  category,
  fullItems,
  courseId,
  isSidePanel,
  isVideoWatched,
  className,
  isLast,
  open,
  onOpenChange,
}: CategoryProps) => {
  const { translate } = useTranslate();
  const lessonIds = getCategoryLessons(category);
  const numLessons = lessonIds.length.toString().padStart(2, "0");
  const isCategoryCompleted = lessonIds.every((lessonId) =>
    isVideoWatched(lessonId),
  );

  return (
    <div key={`category-${category.id}`}>
      <Collapse
        open={open}
        onOpenChange={onOpenChange}
        trigger={
          <div
            className={cn(
              "flex w-full border-collapse cursor-pointer justify-between border-[1px] border-solid border-border bg-surface-secondary px-6 py-5",
              className,
            )}
          >
            <div className="flex items-center">
              <ChevronRight
                className={`mr-2 h-6 w-6 transition-transform duration-200 ${open ? "rotate-90" : "rotate-0"}`}
              />
              <Heading title={category.title} className={isSidePanel ? "h5-bold" : ""} />
            </div>
            <div className="flex items-center">
              <p className="p1-medium text-item-secondary">
                {numLessons} {translate("Lessons")}
              </p>
              {isCategoryCompleted && (
                <Completed className="ml-2 h-6 w-6 flex-shrink-0 text-success-item" />
              )}
            </div>
          </div>
        }
      >
        <div
          className={cn(
            "p1-bold flex flex-col gap-9 border border-solid border-border px-4 py-6",
            isLast && open ? "rounded-bl-xl rounded-br-xl" : "",
          )}
        >
          {category.subcategories.map((subcategory) => {
            return (
              <div key={`subcategory-${subcategory.id}`}>
                <Heading title={subcategory.title} className={cn("pb-4", isSidePanel ? "h5-bold" : "p1-bold")} />
                <ItemList
                  items={subcategory.lessons}
                  fullItems={fullItems}
                  parentId={subcategory.id}
                  courseId={courseId}
                />
              </div>
            );
          })}
        </div>
      </Collapse>
    </div>
  );
};

export const CourseOverview = ({
  collection,
  isSidePanel,
  className,
}: CourseOverviewProps) => {
  const { translate } = useTranslate();
  const { lessons, categories, hasSubcategories } = useCourse(collection);
  const { isVideoWatched } = useCourseCompletion(collection.TabId);

  const fullItems = {
    videos: collection.ChildVideos,
    collections: collection.ChildCollections,
  };

  const [searchParams] = useSearchParams();
  const parentId = searchParams.get("parent_id");

  // Store open category IDs in a Set
  const [openCategoryIds, setOpenCategoryIds] = useState<Set<string>>(new Set());

  const handleCategoryOpenChange = useCallback((id: string, open: boolean) => {
    setOpenCategoryIds((prev) => {
      const next = new Set(prev);
      if (open) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, [setOpenCategoryIds]);

  // Initialize openCategoryIds
  useEffect(() => {
    if (categories && categories.length > 0) {
      if (hasSubcategories && parentId) {
        const foundCategory = categories.find((cat) =>
          cat.subcategories.some((sub) => sub.id === parentId)
        );
        if (foundCategory) {
          setOpenCategoryIds(new Set([foundCategory.id]));
          return;
        }
      }
      setOpenCategoryIds(new Set(categories.map((cat) => cat.id)));
    }
  }, []);


  return (
    <div className={className}>
      <Heading title={translate("Overview")} className={isSidePanel ? "p1-bold pb-1" : "pb-6"} />
      {hasSubcategories ? (
        <div className="overflow-hidden">
          {categories.map((category, idx) => {
            // Compute rounded corners for first and last category
            const roundedClass =
              [
                idx === 0 ? "rounded-tl-xl rounded-tr-xl" : "",
                idx === categories.length - 1
                  ? "data-[state=closed]:rounded-bl-xl data-[state=closed]:rounded-br-xl"
                  : "",
              ]
                .filter(Boolean)
                .join(" ") || undefined;
            return (
              <Category
                key={category.id}
                category={category}
                fullItems={fullItems}
                courseId={collection.TabId}
                isSidePanel={isSidePanel}
                isVideoWatched={isVideoWatched}
                className={roundedClass}
                isLast={idx === categories.length - 1}
                open={openCategoryIds.has(category.id)}
                onOpenChange={(open) => handleCategoryOpenChange(category.id, open)}
              />
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-9 pt-3">
          {categories?.map((category) => (
            <div key={category.id}>
              <Heading title={category.title} className="p1-bold pb-4" />
              <ItemList
                items={category.lessons}
                fullItems={fullItems}
                parentId={category.id}
                courseId={collection.TabId}

              />
            </div>
          ))}
          <ItemList
            items={lessons}
            fullItems={fullItems}
            parentId={collection.TabId}
            courseId={collection.TabId}
          />
        </div>
      )}
    </div>
  );
};
